import { Job, JobQueue, JobQueueService, JobStatus, JobType } from "@app/jobs";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { Flyout, FlyoutContext } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig, SortDirections, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import moment from "moment";
import { FC, useState, useMemo, useEffect, ChangeEvent, useContext, useRef, RefObject, createRef, JSX } from "react";
import { useHistory } from "react-router-dom";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import SummaryTile from "@app/shared/SummaryTile";
import ProjectService from "@app/projects/projectService";
import Project from "@app/projects/project";
import CheckboxDropdown from "@app/shared/checkbox-dropdown/CheckboxDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { JobCancel } from "@app/jobs/JobCancel";
import { JobInfo } from "@app/jobs/JobInfoTab";
import { JobListSummary, JobSummary } from "@app/jobs/jobListSummary";
import { JobProjectInfoTab } from "@app/jobs/JobProjectInfoTab";
import { JobRunConfigTab } from "@app/jobs/JobRunConfigTab";
import JobService from "@app/jobs/jobService";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";

interface JobListTableData {
  [wafflesKeyColumnName]: string;
  jobStatus: string;
  jobStatusDate: string;
  duration: string;
  jobType: string;
  jobQueueName: string;
  clientName: string;
  projectName: string;
  errorMessage: string;
  workerRoleId: string;
  jobId: JSX.Element;
  checkBoxIsDisabled: boolean;
}

interface JobListFilter {
  jobType: string;
  jobStatusDate: string;
  jobQueue: string;
  client: string;
  projectId: string;
  projectName: string;
  status: Array<DropdownItem<JobStatus>>
}

const jobsColumnConfig: ColumnConfig = {
  clientName: {
    displayText: "Client",
    testId: "clientName",
  },
  duration: {
    displayText: "Duration",
    sizeContentToFit: true,
    testId: "duration"
  },
  errorMessage: {
    displayText: "Failure Message",
    minWidth: "500px",
    testId: "errorMessage",
    wrapContent: true
  },
  jobId: {
    displayText: "Job ID",
    testId: "jobId"
  },
  jobQueueName: {
    displayText: "Job Queue",
    testId: "jobQueueName",
  },
  jobStatus: {
    displayText: "Job Status",
    testId: "jobStatus",
  },
  jobStatusDate: {
    displayText: "Job Status Date",
    sizeContentToFit: true,
    testId: "jobStatusDate",
  },
  jobType: {
    displayText: "Job Type",
    testId: "jobType",
  },
  projectId: {
    displayText: "Project ID",
    testId: "projectId",
  },
  projectName: {
    displayText: "Project Name",
    testId: "projectName",
  },
  workerRoleId: {
    displayText: "Worker Host Name",
    testId: "workerRoleId",
  }
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<JobListTableData>,
}

type JobViews = "Job Info" | "Project Info" | "Run Configuration";

const WrappedJobList: FC<React.PropsWithChildren<unknown>> = () => {
  const jobService = new JobService();
  const jobQueueService = new JobQueueService();
  const projectService = new ProjectService();
  const urlParams = new URLSearchParams(window.location.search);

  const flyoutButtonRefs = useRef<Array<RefObject<HTMLButtonElement>>>([]);
  const [currentFlyoutButton, setCurrentFlyoutButton] = useState<number>();
  const [numDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [jobListSummary, setJobListSummary] = useState<JobListSummary>(null);
  const [timerId, setTimerId] = useState(null);
  const [take] = useState(100);
  const [pageSettings, setPageSettings] = useState<PageSettings>(!!urlParams.get("pageSet") ? JSON.parse(urlParams.get("pageSet")) :
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    });
  const [jobStatusDropDownList] = useState<Array<DropdownItem<JobStatus>>>(Object.entries(JobStatus).map(([enumKey, value]) => ({ key: enumKey, name: value })) as Array<DropdownItem<JobStatus>>);
  const [selectedJobStatusItems, setSelectedJobStatusItems] = useState<Array<DropdownItem<JobStatus>>>(JSON.parse(urlParams.get("status")) ?? [{ key: JobStatus.Started, name: JobStatus.Started }]);
  const [jobStatusDate, setJobStatusDate] = useState(urlParams.get("statDate") ?? "");
  const [jobType, setJobType] = useState(urlParams.get("type") ?? "");
  const [jobQueue, setJobQueue] = useState(urlParams.get("queue") ?? "");
  const [jobTypeOptions, setJobTypeOptions] = useState<DropdownItem<string>[]>([{ key: "", name: "All" }]);
  const [client, setClient] = useState(urlParams.get("client") ?? "");
  const [projectId, setProjectId] = useState(urlParams.get("projId") ?? "");
  const [projectName, setProjectName] = useState(urlParams.get("projName") ?? "");
  const [totalRecords, setTotalRecords] = useState(0);
  const [numDispRecLowerBound, setNumDispRecLowerBound] = useState(0);
  const [numDispRecUpperBound, setNumDispRecUpperBound] = useState(0);
  const [jobListFilter, setJobListFilter] = useState<JobListFilter>({
    client: urlParams.get("client"),
    jobQueue: urlParams.get("queue"),
    jobStatusDate: urlParams.get("statDate"),
    jobType: urlParams.get("type"),
    projectId: urlParams.get("projId"),
    projectName: urlParams.get("projName"),
    status: JSON.parse(urlParams.get("status")) ?? [{ key: JobStatus.Started, name: JobStatus.Started }]
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [enableAutoRefresh, setEnableAutoRefresh] = useState(false);
  const [autoRefreshInterval] = useState(30000);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const history = useHistory();
  const [activeView, setActiveView] = useState<JobViews>("Job Info");
  const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
  const [jobInfo, setJobInfo] = useState<{ job: Job, jobSummary: JobSummary }>(null);
  const [projectInfo, setProjectInfo] = useState<Project>(null);
  const [projectRunConfiguration, setProjectRunConfiguration] = useState<ProjectRunConfiguration>(null);
  const [jobQueueList, setJobQueueList] = useState<JobQueue[]>([]);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [selectedStartedJobs, setSelectedStartedJobs] = useState<boolean>(false);
  const flyoutContext = useContext(FlyoutContext);

  const canCancelOrActOnJob = (job: JobSummary) => {
    if ([JobType.Export, JobType.VolumeExport, JobType.ProviderSetVersionParquetCopy, JobType.SnowflakeDataShareRefresh, JobType.SnowflakeDataShareCleanup].includes(job.jobType)) {
      return validExportJobStatuses.includes(job.jobStatus);
    }
    return validJobStatuses.includes(job.jobStatus);
  };

  const validJobStatuses = [JobStatus.Queued, JobStatus.Started];
  const validExportJobStatuses = [JobStatus.Failed, JobStatus.Canceled, JobStatus.Queued, JobStatus.Started];

  useEffect(() => {
    if (!flyoutContext.isFlyoutVisible) {
      setCurrentFlyoutButton(null);
    }
  }, [flyoutContext.isFlyoutVisible])

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: (jobListSummary?.totalNumJobs ?? 0),
  }

    useEffect(() => {
        if (isFirstRun) {
            jobQueueService.getAvailableJobQueues(true).then((queueList) => {
                setJobQueueList(queueList);
                const jobTypes = Object.values(JobType)
                  .sort()
                  .map((type): DropdownItem<string> => ({ key: type, name: type }));
                setJobTypeOptions([{ key: "", name: "All" }, ...jobTypes]);
            });
            return;
        }
        refreshData();
    }, [pageSettings]);

  useEffect(() => {
    if (isFirstRun)
      return;

    if (enableAutoRefresh) {
      setTimerId((prevTimerId: number) => {
        clearTimeout(prevTimerId);
        return setTimeout(refreshData, autoRefreshInterval);
      });
    } else {
      clearTimeout(timerId);
    }
  }, [enableAutoRefresh]);

  useEffect(() => {
    setIsFirstRun(false);
    refreshData();
  }, []);

  const jobListTableData: JobListTableData[] = useMemo<JobListTableData[]>(() => {

    let data: JobListTableData[] = [];

    if (jobListSummary) {
      /* oxlint-disable sort-keys */
      data = jobListSummary.jobList.map((job, index) => {
        return {
          key: job.jobId.toString(),
          jobId: (
            <QESButton
              qesTestId="job-details-content_cancel"
              qesType="secondary"
              onClick={() => handleJobDetailsClick(job, index)}
              qesRef={flyoutButtonRefs.current[index]}
            >
              {job.jobId}
            </QESButton>),
          jobType: job.jobType,
          jobStatus: job.jobStatus,
          jobStatusDate: moment.utc(job.jobStatusDate).local().format("L LT"),
          duration: job.duration,
          jobQueueName: (!!job.jobQueueName ? job.jobQueueName : "--"),
          clientName: job.clientName,
          projectId: job.projectId,
          projectName: job.projectName,
          workerRoleId: job.workerRoleId,
          errorMessage: job.errorMessage,
          checkBoxIsDisabled: !canCancelOrActOnJob(job)
        };
      });
      /* oxlint-enable sort-keys */
    }
    setLoading(false);
    return data;
  }, [jobListSummary]);

  const jobDetailViews: { [key: string]: JSX.Element } = useMemo(() => {
    const views: { [key: string]: JSX.Element } = {
      "Job Info": (
        <PageSection>
          {!!jobInfo ? <JobInfo jobQueueList={jobQueueList} job={jobInfo.job} jobSummary={jobInfo.jobSummary} jobIds={[jobInfo.job.jobId]} onClose={flyoutContext.handleCloseFlyout} /> : null}
        </PageSection>
      )
    };

    if (!!projectInfo) {
      views["Project Info"] = (
        <PageSection>
          <JobProjectInfoTab resource={projectInfo.resource} />
        </PageSection>
      );
    }

    if (!!projectRunConfiguration) {
      views["Run Configuration"] = (
        <PageSection>
          <JobRunConfigTab runConfig={projectRunConfiguration} />
        </PageSection>
      );
    }

    return views;
  }, [jobInfo, projectInfo, projectRunConfiguration, jobQueueList, flyoutContext.isFlyoutVisible]);

  const handleCheckboxChange = (newSelectedIds: string[]) => {
    setSelectedJobIds(newSelectedIds);
    const hasStartedJob = newSelectedIds.some((id) =>
      jobListSummary?.jobList.some((job) => job.jobId.toString() === id && job.jobStatus === JobStatus.Started)
    );
    setSelectedStartedJobs(hasStartedJob);
  };

  const refreshData = () => {
    setSelectedJobIds([]);
    setSelectedStartedJobs(false);
    setLoading(true);

    jobService.getJobListSummary(pageSettings.skip, take, pageSettings.sortDetails.sortDirection, pageSettings.sortDetails.sortedColumn, numDays, jobListFilter.jobType, jobListFilter.jobStatusDate, jobListFilter.jobQueue, jobListFilter.client, !!jobListFilter.projectId ? parseInt(jobListFilter.projectId) : null, jobListFilter.projectName, jobListFilter.status.map(i => i.key), true).then((summary) => {

      setJobListSummary(summary);
      setTotalRecords(summary.totalNumJobs);

      setNumDispRecLowerBound(pageSettings.skip + 1);
      setNumDispRecUpperBound(summary.jobList.length + pageSettings.skip);

      setIsFiltered(!!(jobListFilter.jobType || jobListFilter.jobStatusDate || jobListFilter.jobQueue || jobListFilter.client || jobListFilter.projectId || jobListFilter.projectName || jobListFilter.status.length !== 1 || !jobListFilter.status.some(x => x.key === JobStatus.Started)));

      const newFlyoutRefs = [];
      for (let i = 0; i < summary.totalNumJobs; i += 1) {
        newFlyoutRefs[i] = createRef<HTMLButtonElement>();
      }
      flyoutButtonRefs.current = newFlyoutRefs;

      const searchParams = new URLSearchParams(window.location.search);
      if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
        searchParams.set("pageSet", JSON.stringify(pageSettings));
      } else {
        searchParams.delete("pageSet");
      }
      if (!!jobListFilter.jobType) {
        searchParams.set("type", jobListFilter.jobType);
      } else {
        searchParams.delete("type");
      }
      if (!!jobListFilter.jobStatusDate) {
        searchParams.set("statDate", jobListFilter.jobStatusDate);
      } else {
        searchParams.delete("statDate");
      }
      if (!!jobListFilter.jobQueue) {
        searchParams.set("queue", jobListFilter.jobQueue);
      } else {
        searchParams.delete("queue");
      }
      if (!!jobListFilter.client) {
        searchParams.set("client", jobListFilter.client);
      } else {
        searchParams.delete("client");
      }
      if (!!jobListFilter.projectId) {
        searchParams.set("projId", jobListFilter.projectId);
      } else {
        searchParams.delete("projId");
      }
      if (!!jobListFilter.projectName) {
        searchParams.set("projName", jobListFilter.projectName);
      } else {
        searchParams.delete("projName");
      }
      if (jobListFilter.status.length !== 1 || !jobListFilter.status.some(x => x.key === JobStatus.Started)) {
        searchParams.set("status", JSON.stringify(jobListFilter.status));
      } else {
        searchParams.delete("status");
      }
      history.replace(history.location.pathname + "?" + searchParams.toString());

      if (enableAutoRefresh)
        setTimerId((prevTimerId: number) => {
          clearTimeout(prevTimerId);
          return setTimeout(refreshData, autoRefreshInterval);
        });
    }, () => {
      setJobListSummary(null);
      if (enableAutoRefresh)
        setTimerId((prevTimerId: number) => {
          clearTimeout(prevTimerId);
          return setTimeout(refreshData, autoRefreshInterval);
        });
    });
  }

  const handleSort = (data: JobListTableData[], newSortedColumn: keyof JobListTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleSearch = () => {
    let selectedStatuses = selectedJobStatusItems;
    if (selectedStatuses.length === 0)
      selectedStatuses = jobStatusDropDownList;

    setSelectedJobStatusItems(selectedStatuses);
    setJobListFilter({
        client: client,
        jobQueue: jobQueue,
        jobStatusDate: jobStatusDate,
        jobType: jobType,
        projectId: projectId,
        projectName: projectName,
        status: selectedStatuses
    });

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const clearFilter = () => {
    setSelectedJobStatusItems([{ key: JobStatus.Started, name: JobStatus.Started }]);
    setJobType("");
    setJobStatusDate("");
    setJobQueue("");
    setClient("");
    setProjectId("");
    setProjectName("");

    setJobListFilter({
      client: "",
      jobQueue: "",
      jobStatusDate: "",
      jobType: "",
      projectId: "",
      projectName: "",
      status: [{ key: JobStatus.Started, name: JobStatus.Started }]
    });

    setIsFiltered(false);

    setPageSettings({
      ...pageSettings,
      skip: 0
    });
  }

  const handleSummaryClick = (status: JobStatus) => {
    setClient("");
    setJobQueue("");
    setJobStatusDate("");
    setJobType("");
    setProjectId("");
    setProjectName("");
    setSelectedJobStatusItems([{ key: status, name: status }]);

    setJobListFilter({
      client: "",
      jobQueue: "",
      jobStatusDate: "",
      jobType: "",
      projectId: "",
      projectName: "",
      status: [{ key: status, name: status }]
    });

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleJobDetailsClick = async (jobSummary: JobSummary, index: number) => {
    setJobDetailsLoading(true);
    setActiveView("Job Info");
    setCurrentFlyoutButton(index);
    flyoutContext.setIsFlyoutVisible(true);

    const job = await jobService.getJobById(jobSummary.jobId, true);
    if (!!job.dateStarted) {
      const startTime = moment.utc(job.dateStarted).add(-5, 'seconds').format();
      job.jobLogUrl = job.jobLogUrl + '&from=' + startTime;
    }
    if (!!job.dateFinished) {
      const endTime = moment.utc(job.dateFinished).add(5, 'seconds').format();
      job.jobLogUrl = job.jobLogUrl + '&to=' + endTime;
    }
    else if (!!job.dateCancelled) {
      const endTime = moment.utc(job.dateCancelled).add(5, 'seconds').format();
      job.jobLogUrl = job.jobLogUrl + '&to=' + endTime;
    }
    setJobInfo(
      {
        job: job,
        jobSummary: jobSummary
      }
    );

    if (!!jobSummary.projectId) {
      projectService.getByIdIgnoreAuth(jobSummary.projectId, false).then((projInfo) => {
        setProjectInfo(projInfo);

        projectService.getProjectRunConfigurationByJobAsync(jobSummary.jobId).then((runConfig) => {
          setProjectRunConfiguration(runConfig);
          setJobDetailsLoading(false);
        }, () => {
          setProjectRunConfiguration(null);
          setJobDetailsLoading(false);
        });
      }, () => {
        setProjectInfo(null);
        setProjectRunConfiguration(null);
        setJobDetailsLoading(false);
      });
    } else {
      setProjectInfo(null);
      setProjectRunConfiguration(null);
      setJobDetailsLoading(false);
    }
  }

  const selectedJobTypeItem = useMemo<DropdownItem<string>>(
    () => jobTypeOptions.find((o) => o.key === jobType) ?? { key: "", name: "All" },
    [jobTypeOptions, jobType]
  );

  const jobQueueOptions = useMemo<DropdownItem<string>[]>(
    () => [
      { key: "", name: "All" },
      ...[...jobQueueList]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((q): DropdownItem<string> => ({ key: q.name, name: q.name })),
    ],
    [jobQueueList]
  );

  const selectedJobQueueItem = useMemo<DropdownItem<string>>(
    () => jobQueueOptions.find((o) => o.key === jobQueue) ?? { key: "", name: "All" },
    [jobQueueOptions, jobQueue]
  );

  return (
    <Page documentTitle="Job Listing - Operations">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Job Listing"]} />
      <PageInner>
        <PageHeader title="Job Listing" />
        <PageSection className="mb10">
          <section className="dashboard-summary">
            <SummaryTile
              title="Queued"
              content={<QESButton qesType="secondary" qesTestId="job-list__queued-summary" onClick={() => handleSummaryClick(JobStatus.Queued)}>{jobListSummary?.numQueued}</QESButton>}
            />
            <SummaryTile
              title="Started"
              content={<QESButton qesType="secondary" qesTestId="job-list__queued-summary" onClick={() => handleSummaryClick(JobStatus.Started)}>{jobListSummary?.numStarted}</QESButton>}
            />
            <SummaryTile
              title="Finished"
              content={<QESButton qesType="secondary" qesTestId="job-list__queued-summary" onClick={() => handleSummaryClick(JobStatus.Finished)}>{jobListSummary?.numFinished}</QESButton>}
            />
            <SummaryTile
              title="Failed"
              content={<QESButton qesType="secondary" qesTestId="job-list__queued-summary" onClick={() => handleSummaryClick(JobStatus.Failed)}>{jobListSummary?.numFailed}</QESButton>}
            />
            <SummaryTile
              title="Cancelled"
              content={<QESButton qesType="secondary" qesTestId="job-list__queued-summary" onClick={() => handleSummaryClick(JobStatus.Canceled)}>{jobListSummary?.numCancelled}</QESButton>}
            />
          </section>
        </PageSection>
        <PageSection>
          <div className="job-list-filters mb-3 mt-5">
            <SingleSelectDropdown<string>
              containerClassName="job-list-filters__input"
              customWidth={true}
              label="Job Type"
              items={jobTypeOptions}
              selectedItem={selectedJobTypeItem}
              onSelectionChanged={(item) => setJobType(item?.key ?? "")}
              testId="job-list-filter__job-type"
              disabled={loading}
            />
            <QESDateInput
              containerClassName="job-list-filters__date-input"
              max={moment().format("YYYY-MM-DD")}
              label="Job Status Date"
              testId="job-status-date"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setJobStatusDate(e.target.value)}
              onKeyUp={(e) => handleSearchKeyUp(e)}
              value={jobStatusDate}
            />
            <SingleSelectDropdown<string>
              containerClassName="job-list-filters__input"
              customWidth={true}
              label="Job Queue"
              items={jobQueueOptions}
              selectedItem={selectedJobQueueItem}
              onSelectionChanged={(item) => setJobQueue(item?.key ?? "")}
              testId="job-list-filter__job-queue"
              disabled={loading}
            />
            <QESTextInput containerClassName="job-list-filters__input" label="Client" testId="job-list-filter__client-name" value={client} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setClient(e.target.value)} />
            <QESTextInput containerClassName="job-list-filters__input" label="Project ID" testId="job-list-filter__project-id" value={projectId} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setProjectId(e.target.value)} />
            <QESTextInput containerClassName="job-list-filters__input" label="Project Name" testId="job-list-filter__project-name" value={projectName} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setProjectName(e.target.value)} />
            <CheckboxDropdown
              buttonClassNames="me-2"
              buttonText="Job Status"
              cancelText="Cancel"
              disableEmptySelection={true}
              loading={loading}
              items={jobStatusDropDownList}
              selectedItems={selectedJobStatusItems}
              hideButtons={true}
              onSelectionChanged={(selectedItems: Array<DropdownItem<JobStatus>>) => { setSelectedJobStatusItems(selectedItems); }}
            />
            <QESButton qesType="primary" qesTestId="job-list__search-button" onClick={handleSearch} disabled={loading}>Search</QESButton>
            <QESButton qesType="secondary" qesTestId="job-list__clear-filters" onClick={clearFilter} disabled={!isFiltered || loading}>Clear</QESButton>
          </div>
          <div className="job-list-filters mb-3">
            <QESButton qesType="primary" qesTestId="job-list__refresh-button" onClick={handleSearch} disabled={loading}>Refresh</QESButton>
            <QESCheckbox
              containerClassName="ms-2 pb-2"
              id="sso-isEnabled"
              label={<span className="align-middle">{enableAutoRefresh ? "Auto Refresh Enabled" : "Auto Refresh Disabled"}</span>}
              checked={enableAutoRefresh}
              onChange={(e) => { setEnableAutoRefresh(e.target.checked); }}
              disabled={loading}
              aria-label="Enable Auto Refresh"
              testId="job-list__auto-refresh-enabled"
            />
          </div>
          <div className="row justify-content-between mb-3">
            <div className="col-md-auto">
              <JobCancel jobIds={selectedJobIds.map(jobId => parseInt(jobId, 10))} jobQueueList={jobQueueList} started={selectedStartedJobs} />
            </div>
            <div className="col text-end mb-0 align-text-bottom">
              <span>Displaying {totalRecords > 0 ? `${numDispRecLowerBound}-${numDispRecUpperBound} of ${totalRecords}` : "0"} jobs {isFiltered ? "(filtered)" : ""} since {moment().subtract(numDays, "days").format("MM/DD/YYYY")}</span>
            </div>
          </div>
          <Waffles<JobListTableData>
            columns={jobsColumnConfig}
            customSort={handleSort}
            data={jobListTableData}
            enablePagination={true}
            loading={loading}
            passThroughSorting={true}
            paginationProps={paginationProps}
            testId="jobs-waffles-table"
            invertInitialSort={true}
            showCheckboxes={true}
            handleCheckboxChange={handleCheckboxChange}
            selectedKeys={selectedJobIds}
          />
          <Flyout
            content={{
              body: jobDetailsLoading ? (<LoadingSpinner />) : (
                <>
                  <div className="tabs-style-2">
                    <ul className="nav nav-tabs">
                      {Object.keys(jobDetailViews).map((key) => (
                        <li className="nav-item" key={key}>
                          <a className={`nav-link${activeView === key ? " active" : ""}`} role="tab" onClick={() => setActiveView(key as JobViews)}>{key}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="row mt-3 ml10 mr10">
                    {jobDetailViews[activeView]}
                  </div>
                </>
              ),
              title: "Job Details",
            }}
            triggerElement={flyoutButtonRefs.current[currentFlyoutButton]}
          />
        </PageSection>
      </PageInner>
    </Page>
  );
}

export const JobList: FC<unknown> = () => (
  <FlyoutContextProvider>
    <WrappedJobList />
  </FlyoutContextProvider>
);